import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
// CDK overlay removed — dropdowns are rendered inline
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Country } from '../../../../model/country.model';
import { District } from '../../../../model/district.model';
import { Region } from '../../../../model/region.model';
import { SearchService } from './search.service';
import { filter, Subject, take } from 'rxjs';
import { ScreenSizeService } from '../../../../services/utils/screenSize.service';
import { SearchBarService } from '../../../../services/utils/searchBar.service';
import { LocalizedRouterService } from '../../../localized-router.service';
import { OffcanvasHistoryService } from '../../../../services/offcanvas-history.service';
import { SelectOffcanvasComponent } from '../select-offcanvas/select-offcanvas.component';

@Component({
  selector: 'app-searchbar',
  templateUrl: './searchbar.component.html',
  styleUrls: ['./searchbar.component.scss'],
  standalone: false,
  animations: [
    trigger('dropdownAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms ease', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('150ms ease', style({ opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ])
  ]
})
export class SearchBarComponent implements OnInit {

  constructor(
    private router: Router,
    private eRef: ElementRef,
    private translate: TranslateService,
    private searchService: SearchService,
    protected screenSize: ScreenSizeService,
    protected searchFlow: SearchBarService,
    private activatedRoute: ActivatedRoute,
    private localizedRouter: LocalizedRouterService,
    private offcanvasHistoryService: OffcanvasHistoryService
  ) { }

  @Output() openModal = new EventEmitter<string>();
  @ViewChild('searchButton', { static: false }) searchButtonRef?: ElementRef<HTMLButtonElement>;
  searchParams: { region?: string, country?: string, city?: string } = {};

  items?: any;
  dropdown: string = '';
  loadingCountries: boolean = true;
  loadingRegions: boolean = true;
  loadingDistricts: boolean = true;
  @Input() isHomePage: boolean = false;
  @Input() isMobileOffcanvas: boolean = false;
  @Output() toggleFiltering = new EventEmitter<boolean>();
  private destroy$ = new Subject<void>();
  modalTitle: string = '';

  async ngOnInit(): Promise<void> {
    this.searchFlow.dataReady$
      .pipe(filter(ready => ready === true), take(1))
      .subscribe(async () => {


        if (this.searchFlow.selectedCountry) {
          await this.selectCountry(this.searchFlow.selectedCountry, true);
        }

        if (this.searchFlow.selectedRegion) {
          await this.selectRegion(this.searchFlow.selectedRegion);
        }

        if (this.searchFlow.selectedDistrict) {
          await this.selectDistrict(this.searchFlow.selectedDistrict);
        }

        this.loadingCountries = false;
        this.loadingRegions = false;
        this.loadingDistricts = false;
      });
  }

  setDefaultValues() {
    const region = this.searchFlow.allRegions.find(r => r.id === this.searchService.searchResult?.region?.id)
    const district = this.searchFlow.allDistricts.find(d => d.id === this.searchService.searchResult?.district?.id)
    if (region) this.searchFlow.selectedRegion = region;
    if (district) this.searchFlow.selectedDistrict = district;
  }


  async selectCountry(country: Country, onlyCountries: boolean = false) {

    if (country.id !== this.searchFlow.selectedCountry?.id) {
      this.searchFlow.selectedCountry = country;
      this.searchFlow.selectedDistrict = undefined;
      if (!onlyCountries) this.loadRegions(country.id);
    }
  }

  async selectDistrict(district?: District) {

    if (district?.id !== this.searchFlow.selectedDistrict?.id) {
      this.searchFlow.selectedDistrict = district;
    }
  }

  async selectRegion(region?: Region) {

    if (region && region?.id !== this.searchFlow.selectedRegion?.id) {
      this.searchFlow.selectedRegion = region;

    }
    if (region === undefined) {
      this.searchFlow.selectedRegion = undefined;
      this.searchFlow.selectedDistrict = undefined;
      this.searchFlow.allDistricts = [];
    }
  }

  toggleDropdown(type: string, event?: Event) {
    event?.stopPropagation();
    if ((this.screenSize.isMobile || this.screenSize.isTablet) && (type === 'country' || type === 'region')) {
      this.openOffcanvasForSelection(type);
      return;
    }
    if (this.dropdown === type) {
      this.dropdown = '';
    } else {
      this.dropdown = type;
    }
  }

  private openOffcanvasForSelection(type: 'country' | 'region' | 'district') {
    const offcanvasRef = this.offcanvasHistoryService.open(SelectOffcanvasComponent, {
      panelClass: 'app-offcanvas overflow-hidden',
      position: 'bottom',
      scroll: false,
      backdrop: true,
      backdropClass:
        type == 'country' || type == 'region' || type == 'district'
          ? 'bg-transparent'
          : '',

    });

    const component = offcanvasRef.componentInstance as SelectOffcanvasComponent;

    component.type = type;
    switch (type) {
      case 'country':
        component.items = this.searchFlow.allCountries;
        component.selectedItem = this.searchFlow.selectedCountry;
        component.title = this.translate.instant('Choose Country');
        break;
      case 'region':
        component.items = this.searchFlow.allRegions;
        component.selectedItem = this.searchFlow.selectedRegion;
        component.title = this.translate.instant('Choose Region');
        break;
      case 'district':
        component.items = this.searchFlow.allDistricts;
        component.selectedItem = this.searchFlow.selectedDistrict;
        component.title = this.translate.instant('Choose District');
        break;
    }

    offcanvasRef.result.then(
      (result: { item: any, type: string }) => {
        if (result) {
          this.handleSelectByType(type, result.item);
        }
      },
      () => {

      }
    );
  }

  public handleSelectByType(type: string, item: any) {
    switch (type) {
      case 'country':
        this.selectCountry(item);
        break;
      case 'region':
        this.selectRegion(item);
        break;
      case 'district':
        this.selectDistrict(item);
        break;
      default:
        console.warn('Unknown dropdown type:', this.dropdown);
    }

    if (!this.isHomePage && this.isSearchButtonHidden()) {
      this.navigateToSearch();
    }
  }

  public handleSelect(item: any) {
    switch (this.dropdown) {
      case 'country':
        this.selectCountry(item);
        break;
      case 'region':
        this.selectRegion(item);
        break;
      case 'district':
        this.selectDistrict(item);
        break;
      default:
        console.warn('Unknown dropdown type:', this.dropdown);
    }

    if (!this.isHomePage && this.isSearchButtonHidden()) {
      this.navigateToSearch();
    }
  }

  private isSearchButtonHidden(): boolean {
    if (!this.searchButtonRef?.nativeElement) {
      return true;
    }

    const button = this.searchButtonRef.nativeElement;

    return button.offsetWidth === 0 || button.offsetHeight === 0;
  }

  navigateToSearch() {
    const currentPath = this.router.url.split('?')[0];
    const queryParams = {
      country: this.searchFlow.selectedCountry?.id,
      region: this.searchFlow.selectedRegion?.id,
      district: this.searchFlow.selectedDistrict?.id,
      sort: 'averageRate-desc',
      language: 'wl'
    };
    if (currentPath.includes('/search')) {
      this.localizedRouter.navigate(['search'], { queryParams });
      this.searchFlow.triggerCloseOffcanvas();
    } else {
      this.localizedRouter.navigate(['search'], { queryParams });
    }
  }
  areSearchParamsSame(): boolean {
    const queryParams = this.activatedRoute.snapshot.queryParams;

    const country = String(this.searchFlow.selectedCountry?.id ?? '');
    const region = String(this.searchFlow.selectedRegion?.id ?? '');
    const district = String(this.searchFlow.selectedDistrict?.id ?? '');
    const sort = `${this.searchFlow.selectedSort}-${this.searchFlow.selectedDirection}`;
    const language = this.searchFlow.selectedLanguage ?? '';

    return (
      (queryParams['country'] ?? '') === country &&
      (queryParams['region'] ?? '') === region &&
      (queryParams['district'] ?? '') === district &&
      (queryParams['sort'] ?? '') === sort &&
      (queryParams['language'] ?? '') === language
    );
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!this.eRef.nativeElement.contains(event.target) || target?.id === 'searchButton') {
      this.dropdown = '';
    }
  }

  get districtAndCountry(): string {
    const country = this.searchFlow.selectedCountry?.name ?? '';
    const district = this.searchFlow.selectedDistrict?.name;

    return district ? `${district}, ${country}` : `${this.translate.instant('All')} ${country}`;
  }



  async loadCountries(onlyCountries: boolean = false) {
    try {
      this.loadingCountries = true;
      this.loadingRegions = true;
      this.loadingDistricts = true;
      this.searchFlow.allCountries = this.searchFlow.allCountries;
      if (this.searchFlow.selectedCountry) {
        this.selectCountry(this.searchFlow.selectedCountry, onlyCountries);
      }
      this.loadingCountries = false;

    } catch (error) {
      console.error(error);
    }
  }

  async loadRegions(countryId?: string) {
    try {
      if (!countryId) {
        return;
      }
      this.loadingRegions = true;
      this.loadingDistricts = true;
      this.searchFlow.selectedDistrict = undefined;
      this.searchFlow.allRegions = this.searchFlow.allRegions;
      if (this.searchFlow.selectedRegion) {
        this.loadDistricts(this.searchFlow.selectedRegion?.id);
      } else {
        this.loadingDistricts = false;
      }
      this.loadingRegions = false;
    } catch (error) {
      console.error(error);

    }
  }

  async loadDistricts(regionId?: string) {
    try {
      this.loadingDistricts = true;
      this.searchFlow.selectedDistrict = undefined;
      this.searchFlow.allDistricts = this.searchFlow.allDistricts;
      this.loadingDistricts = false;
    } catch (error) {
      console.error(error);
    }
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


}
